// Preferências do usuário (Zustand + persist em localStorage). Sobrevive a reload.
// Hoje: tema (claro/escuro). Ponto natural pra crescer (idioma, densidade, etc.).
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark";
export type Language = "pt-BR" | "en" | "es";
export type VisualizerVariant = "bars" | "wave";
export type CallGuideTab = "script" | "objections";

interface PreferencesState {
  theme: Theme;
  language: Language;
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
  toggleTheme: () => void;
  visualizer: VisualizerVariant;
  setVisualizer: (variant: VisualizerVariant) => void;
  // Volume do ringback (tom de "chamando") — preferência POR USUÁRIO, 0..1.
  ringbackVolume: number;
  setRingbackVolume: (v: number) => void;
  // Exibir o painel de apoio da org (script e objeções) durante a ligação
  // (Discador e Softphone). Quem não usa tira da tela sem mexer na organização.
  showSalesScript: boolean;
  setShowSalesScript: (v: boolean) => void;
  // Aba em que o painel de apoio ABRE. Vendedor que trabalha rebatendo objeção
  // começa nelas; quem segue o roteiro começa no script. Trocar de aba durante a
  // sessão continua valendo — isto só decide por onde começa.
  callGuideTab: CallGuideTab;
  setCallGuideTab: (v: CallGuideTab) => void;
  // Discador: avançar para o próximo lead automaticamente após tabular.
  autoNextDialer: boolean;
  setAutoNextDialer: (v: boolean) => void;
}

export function migratePreferences(
  persistedState: unknown,
  version: number,
): Partial<PreferencesState> {
  const state = persistedState as Partial<PreferencesState>;
  return {
    ...state,
    ...(version < 1 ? { autoNextDialer: true } : null),
    ...(version < 2 ? { language: "pt-BR" as const } : null),
  };
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      theme: "dark", // default = escuro (visual atual do app)
      language: "pt-BR",
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      toggleTheme: () => set((s) => ({ theme: s.theme === "dark" ? "light" : "dark" })),
      visualizer: "bars", // default = barras (o visual clássico)
      setVisualizer: (visualizer) => set({ visualizer }),
      ringbackVolume: 0.5, // default = meio-termo
      setRingbackVolume: (ringbackVolume) =>
        set({ ringbackVolume: Math.max(0, Math.min(1, ringbackVolume)) }),
      showSalesScript: true, // default = ativo (comportamento atual)
      setShowSalesScript: (showSalesScript) => set({ showSalesScript }),
      callGuideTab: "script", // default = script (a aba que já abria)
      setCallGuideTab: (callGuideTab) => set({ callGuideTab }),
      autoNextDialer: true,
      setAutoNextDialer: (autoNextDialer) => set({ autoNextDialer }),
    }),
    {
      name: "lai-disk-preferences",
      version: 2,
      // A versão anterior persistia `false` como default, apesar de a tela
      // documentar o discador automático como ligado por padrão. Migra inclusive
      // quem já abriu o app, para não ficar preso no falso estado de fila vazia.
      migrate: migratePreferences,
    },
  ),
);
